import {
  MdClose,
  MdConnectWithoutContact,
  MdPerson,
  MdSend,
} from "react-icons/md";
import Modal from "../Modal/Modal";
import { useTranslation } from "react-i18next";
import { FILE_STORAGE_SERVER_URL, ICON_SIZES } from "src/helpers/constants";
import useInvites from "src/hooks/useInvites";
import useUsers from "src/hooks/useUsers";
import { useMemo, useState } from "react";
import clsx from "clsx";
import CheckBox from "../CheckBox/CheckBox";
import { sortBy } from "lodash";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { getShortName } from "src/helpers/utils";
import RadioInput from "../RadioInput/RadioInput";
import { EButtonKind } from "../Button/ButtonTypes";

interface IShareTransactionModal {
  onClose: () => void;
  isMultiple?: boolean;
  onSend: (data: Array<any> | string) => void;
  isLoading?: boolean;
}

const ShareTransactionModal: React.FC<IShareTransactionModal> = ({
  onClose,
  isMultiple = false,
  onSend,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const { acceptedInvites } = useInvites();
  const { getUserById } = useUsers();
  const [selectedContacts, setSelectedContacts] = useState<any[]>([]);
  const [selectedContact, setSelectedContact] = useState<string>("");
  const navigate = useNavigate();

  const availableContacts = useMemo(() => {
    return sortBy(
      acceptedInvites?.map?.((invite) => {
        const user = getUserById({
          _id: invite.iSentTheInvite ? invite.from : invite.to,
        });
        return {
          id: user?._id,
          name: user?.name,
          email: user?.email,
          picture: user?.picture,
        };
      }) || [],
      ["name"]
    );
  }, [acceptedInvites, getUserById]);

  return (
    <Modal onEscape={() => onClose()} className={clsx(["md:!w-[450px]"])}>
      <Modal.Header onClose={() => onClose()} title="Enviar a alguien" />
      <Modal.Body>
        <div className="flex flex-col gap-4">
          {availableContacts?.length ? (
            <>
              <div
                className={clsx([
                  "grid",
                  "grid-cols-1",
                  "w-full",
                  "gap-2",
                  "md:gap-2",
                ])}
              >
                {availableContacts.map((el: any, key: number) => (
                  <motion.div
                    key={key}
                    className={clsx([
                      "w-full",
                      "gap-4",
                      "items-center",
                      "flex",
                      "cursor-pointer",
                    ])}
                    onClick={() => {
                      if (isMultiple) {
                        setSelectedContacts((prev) => {
                          if (prev.includes(el.id)) {
                            return prev.filter((id) => id !== el.id);
                          }
                          return [...prev, el.id];
                        });
                      } else {
                        if (selectedContact === el.id) {
                          setSelectedContact("");
                        } else {
                          setSelectedContact(el.id);
                        }
                      }
                    }}
                    initial={{
                      opacity: 0,
                      x: key % 2 === 0 ? -100 : 100,
                    }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: 0.2,
                      ease: "easeOut",
                      delay: key * 0.1,
                    }}
                  >
                    <div
                      className={clsx([
                        "flex",
                        "flex-shrink-0",
                        "!h-[50px]",
                        "!w-[50px]",
                        "md:!h-[60px]",
                        "md:!w-[60px]",
                      ])}
                    >
                      {el?.picture ? (
                        <img
                          src={`${FILE_STORAGE_SERVER_URL}/profile-thumbnail-${el.picture}`}
                          alt={el.name}
                          className={clsx([
                            {
                              "!h-[50px] !w-[50px] md:!h-[60px] md:!w-[60px] rounded-md":
                                true,
                            },
                            "object-cover",
                            "overflow-hidden",
                            "bg-checkers",
                          ])}
                        />
                      ) : (
                        <MdPerson size={ICON_SIZES.MD} />
                      )}
                    </div>
                    <div
                      className={clsx([
                        "flex",
                        "flex-row",
                        "gap-2",
                        "items-center",
                        "w-full",
                        "justify-between",
                      ])}
                    >
                      <div className="flex flex-col text-sm md:text-base">
                        <div className="font-normal">
                          {getShortName(el.name)}
                        </div>
                        <div className="text-xs dark:text-cyan-400 text-blue-400">
                          {el.name}
                        </div>
                      </div>
                      {isMultiple ? (
                        <CheckBox
                          checked={selectedContacts.includes(el.id)}
                          onChange={() => {}}
                        />
                      ) : (
                        <div>
                          <RadioInput
                            selected={selectedContact === el.id}
                            value={el.id}
                            onClick={() => {
                              if (selectedContact === el.id) {
                                setSelectedContact("");
                              } else {
                                setSelectedContact(el.id);
                              }
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
              {availableContacts?.length > 1 && (
                <div className={clsx(["text-xs", "md:text-sm"])}>
                  Puedes compartir transacciones sólo con un contacto a la vez.
                </div>
              )}
            </>
          ) : (
            <div className="text-sm md:text-sm">
              Debes tener al menos un contacto en tu red de contactos para poder
              compartir la transacción.
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer
        actions={[
          {
            icon: <MdSend size={ICON_SIZES.MD} />,
            onClick: () => {
              onSend(isMultiple ? selectedContacts : selectedContact);
            },
            text: t("Enviar"),
            kind: EButtonKind.SAVE,
            disabled:
              (isMultiple ? selectedContacts.length === 0 : !selectedContact) ||
              isLoading,
            hide: availableContacts?.length === 0,
            isLoading,
          },
          {
            icon: <MdConnectWithoutContact size={ICON_SIZES.MD} />,
            onClick: () => {
              navigate("/settings/contacts");
            },
            text: t("Invitar"),
            kind: EButtonKind.SAVE,
            hide: !!availableContacts?.length,
          },
          {
            icon: <MdClose size={ICON_SIZES.MD} />,
            onClick: () => onClose(),
            text: t("common_buttons.close"),
            disabled: isLoading,
          },
        ]}
      />
    </Modal>
  );
};

export default ShareTransactionModal;
